import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Snackbar, Paper, Switch, FormControlLabel,
  IconButton
} from '@mui/material';
import { Add, Edit, Delete, Close, Image as ImageIcon } from '@mui/icons-material';
import { bannersAPI, uploadAPI } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';

export default function Banners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const emptyForm = {
    _id: '', title: '', imageUrl: '', linkUrl: '', isActive: true,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
  };
  const [form, setForm] = useState(emptyForm);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });

  useEffect(() => { loadBanners(); }, []);

  const loadBanners = async () => {
    setLoading(true);
    try {
      const res = await bannersAPI.getAll();
      setBanners(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (banner = null) => {
    if (banner) {
      setIsEditing(true);
      setForm({
        ...banner,
        startDate: new Date(banner.startDate).toISOString().split('T')[0],
        endDate: new Date(banner.endDate).toISOString().split('T')[0],
      });
      setImagePreview(null); // Assuming existing imageUrl is enough
    } else {
      setIsEditing(false);
      setForm(emptyForm);
      setImagePreview(null);
    }
    setError('');
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      setForm(f => ({ ...f, imageUrl: res.data.data.url }));
      setImagePreview(URL.createObjectURL(file));
    } catch (err) {
      setError('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.imageUrl || !form.startDate || !form.endDate) {
      setError('Title, Image, Start Date, and End Date are required');
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await bannersAPI.update(form._id, form);
      } else {
        await bannersAPI.create(form);
      }
      setSnackbar({ open: true, message: `Banner ${isEditing ? 'updated' : 'created'}` });
      setDialogOpen(false);
      loadBanners();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({ open: true, id });
  };

  const doDelete = async () => {
    try {
      await bannersAPI.delete(confirmDialog.id);
      setSnackbar({ open: true, message: 'Banner deleted' });
      loadBanners();
    } catch (err) {
      console.error(err);
    } finally {
      setConfirmDialog({ open: false, id: null });
    }
  };

  const serverUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

  return (
    <Box>
      <PageHeader
        title="Banners"
        subtitle="Manage promotional banners shown on the mobile app home screen."
        gradient="linear-gradient(90deg, #C2410C, #FB923C)"
        actions={
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => handleOpenDialog()}>Add Banner</Button>
        }
      />

      {loading ? <Typography>Loading...</Typography> : (
        <Grid container spacing={2}>
          {banners.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 5 }}>No banners found.</Typography>
            </Grid>
          )}
          {banners.map(banner => (
            <Grid item xs={12} sm={6} md={4} key={banner._id}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                  sx={{
                    height: 140,
                    bgcolor: 'grey.100',
                    backgroundImage: `url(${banner.imageUrl.startsWith('http') ? banner.imageUrl : serverUrl + banner.imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="subtitle1" fontWeight={600} noWrap>{banner.title}</Typography>
                    <FormControlLabel
                      control={<Switch size="small" checked={banner.isActive} readOnly />}
                      label={<Typography variant="caption">{banner.isActive ? 'Active' : 'Inactive'}</Typography>}
                      sx={{ ml: 1, mr: 0 }}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {new Date(banner.startDate).toLocaleDateString()} - {new Date(banner.endDate).toLocaleDateString()}
                  </Typography>
                  {banner.linkUrl && (
                    <Typography variant="caption" color="primary" noWrap display="block" sx={{ mt: 0.5 }}>
                      Link: {banner.linkUrl}
                    </Typography>
                  )}
                </CardContent>
                <Stack direction="row" spacing={1} sx={{ p: 2, pt: 0 }}>
                  <Button size="small" startIcon={<Edit />} onClick={() => handleOpenDialog(banner)}>Edit</Button>
                  <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDelete(banner._id)}>Delete</Button>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{isEditing ? 'Edit Banner' : 'Add Banner'}</Typography>
            <IconButton onClick={() => setDialogOpen(false)}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Stack spacing={2}>
            <Box>
              {form.imageUrl ? (
                <Box
                  sx={{
                    width: '100%', height: 160, borderRadius: 2, mb: 1,
                    backgroundImage: `url(${imagePreview || (form.imageUrl.startsWith('http') ? form.imageUrl : serverUrl + form.imageUrl)})`,
                    backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid', borderColor: 'divider'
                  }}
                />
              ) : (
                <Box sx={{ width: '100%', height: 160, borderRadius: 2, mb: 1, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed', borderColor: 'grey.400' }}>
                  <ImageIcon color="disabled" sx={{ fontSize: 40 }} />
                </Box>
              )}
              <Button variant="outlined" component="label" disabled={imageUploading} fullWidth>
                {imageUploading ? 'Uploading...' : 'Upload Image'}
                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
              </Button>
            </Box>

            <TextField label="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} fullWidth size="small" />
            <TextField label="Link URL (Optional)" value={form.linkUrl} onChange={e => setForm({ ...form, linkUrl: e.target.value })} fullWidth size="small" placeholder="https://..." />
            
            <Stack direction="row" spacing={2}>
              <TextField label="Start Date" type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} fullWidth size="small" InputLabelProps={{ shrink: true }} />
              <TextField label="End Date" type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            </Stack>
            
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Banner'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} />

      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete Banner"
        message="Are you sure you want to delete this banner? This cannot be undone."
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setConfirmDialog({ open: false, id: null })}
      />
    </Box>
  );
}
